import type {
  ConfigChange,
  EngineConfig,
  EngineStats,
  ExportOptions,
  PhysicalOptions,
  PhysicalPlan,
  PrepareStats,
  PreprocessSettings,
  RenderSettings,
  SourceMetadata,
  StepInfo,
} from '@/types/engine';
import {
  isResponse,
  transferablesOf,
  type CompleteMessage,
  type ProgressMessage,
  type SolutionSnapshot,
  type WorkerMessage,
  type WorkerRequest,
  type WorkerResponse,
} from './workerProtocol';

export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  terminate(): void;
}

export interface EngineClientEvents {
  progress: ProgressMessage;
  complete: CompleteMessage;
  paused: { iteration: number; error: number };
  cancelled: { iteration: number; error: number };
  error: { message: string };
  status: { running: boolean };
}

type Listener<K extends keyof EngineClientEvents> = (payload: EngineClientEvents[K]) => void;

type ResponseOf<T extends WorkerResponse['type']> = Extract<WorkerResponse, { type: T }>;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type RequestBody = DistributiveOmit<WorkerRequest, 'id'>;

interface Pending {
  expect: WorkerResponse['type'];
  resolve: (value: WorkerResponse) => void;
  reject: (err: Error) => void;
}

export function createDefaultWorker(): WorkerLike {
  return new Worker(new URL('./stringArt.worker.ts', import.meta.url), { type: 'module', name: 'string-art-engine' });
}

export class EngineClient {
  private readonly worker: WorkerLike;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private readonly listeners: { [K in keyof EngineClientEvents]: Set<Listener<K>> } = {
    progress: new Set(),
    complete: new Set(),
    paused: new Set(),
    cancelled: new Set(),
    error: new Set(),
    status: new Set(),
  };
  private running = false;
  private disposed = false;

  constructor(worker: WorkerLike = createDefaultWorker()) {
    this.worker = worker;
    worker.onmessage = (event: MessageEvent<WorkerMessage>) => this.dispatch(event.data);
    worker.onerror = (event: ErrorEvent) => {
      this.emit('error', { message: `worker crashed: ${event.message ?? 'unknown error'}` });
      this.rejectAll(new Error(`worker crashed: ${event.message ?? 'unknown error'}`));
      this.setRunning(false);
    };
  }

  get isRunning(): boolean {
    return this.running;
  }

  on<K extends keyof EngineClientEvents>(event: K, listener: Listener<K>): () => void {
    this.listeners[event].add(listener);
    return () => this.listeners[event].delete(listener);
  }

  private emit<K extends keyof EngineClientEvents>(event: K, payload: EngineClientEvents[K]): void {
    for (const l of this.listeners[event]) {
      try {
        l(payload);
      } catch (err) {
        console.error(`listener for ${event} failed`, err);
      }
    }
  }

  private setRunning(running: boolean): void {
    if (this.running !== running) {
      this.running = running;
      this.emit('status', { running });
    }
  }

  private dispatch(message: WorkerMessage): void {
    if (message.type === 'progress') {
      this.setRunning(true);
      this.emit('progress', message);
      return;
    }
    if (message.type === 'complete') {
      this.setRunning(false);
      this.emit('complete', message);
      return;
    }
    if (!isResponse(message)) return;
    if (message.type === 'error') {
      if (message.id !== null && this.pending.has(message.id)) {
        const p = this.pending.get(message.id)!;
        this.pending.delete(message.id);
        p.reject(new Error(message.message));
      } else {
        this.emit('error', { message: message.message });
      }
      if (message.id === null) this.setRunning(false);
      return;
    }
    if (message.type === 'paused') {
      this.setRunning(false);
      this.emit('paused', { iteration: message.iteration, error: message.error });
    } else if (message.type === 'cancelled') {
      this.setRunning(false);
      this.emit('cancelled', { iteration: message.iteration, error: message.error });
    } else if (message.type === 'started') {
      this.setRunning(true);
    }
    const p = this.pending.get(message.id);
    if (!p) return;
    this.pending.delete(message.id);
    if (p.expect !== message.type) {
      p.reject(new Error(`expected ${p.expect} but received ${message.type}`));
      return;
    }
    p.resolve(message);
  }

  private rejectAll(err: Error): void {
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
  }

  private request<T extends WorkerResponse['type']>(expect: T, body: RequestBody): Promise<ResponseOf<T>> {
    if (this.disposed) return Promise.reject(new Error('engine client is disposed'));
    const id = this.nextId++;
    const message = { ...body, id } as WorkerRequest;
    return new Promise<ResponseOf<T>>((resolve, reject) => {
      this.pending.set(id, { expect, resolve: (v) => resolve(v as ResponseOf<T>), reject });
      try {
        this.worker.postMessage(message, transferablesOf(message));
      } catch (err) {
        this.pending.delete(id);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  async initialize(): Promise<string> {
    return (await this.request('initialized', { type: 'initialize' })).version;
  }

  async loadTarget(rgba: Uint8Array, width: number, height: number, config: EngineConfig): Promise<Uint8Array> {
    return (await this.request('targetLoaded', { type: 'loadTarget', rgba, width, height, config })).processed;
  }

  async preview(rgba: Uint8Array, width: number, height: number, preprocess: PreprocessSettings): Promise<Uint8Array> {
    return (await this.request('preview', { type: 'preview', rgba, width, height, preprocess })).gray;
  }

  async configure(config: EngineConfig): Promise<ConfigChange> {
    return (await this.request('configured', { type: 'configure', config })).change;
  }

  async generatePins(): Promise<Float32Array> {
    return (await this.request('pins', { type: 'generatePins' })).pins;
  }

  async prepare(): Promise<{ stats: PrepareStats; pins: Float32Array; target: Uint8Array }> {
    const r = await this.request('prepared', { type: 'prepare' });
    return { stats: r.stats, pins: r.pins, target: r.target };
  }

  async start(): Promise<void> {
    await this.request('started', { type: 'start' });
  }

  async pause(): Promise<{ iteration: number; error: number }> {
    const r = await this.request('paused', { type: 'pause' });
    return { iteration: r.iteration, error: r.error };
  }

  async cancel(): Promise<{ iteration: number; error: number }> {
    const r = await this.request('cancelled', { type: 'cancel' });
    return { iteration: r.iteration, error: r.error };
  }

  async step(): Promise<{ step: StepInfo; current: Uint8Array }> {
    const r = await this.request('stepped', { type: 'step' });
    return { step: r.step, current: r.current };
  }

  async replay(sequence: Uint16Array): Promise<{ iteration: number; error: number; current: Uint8Array; currentPin: number }> {
    const r = await this.request('replayed', { type: 'replay', sequence });
    return { iteration: r.iteration, error: r.error, current: r.current, currentPin: r.currentPin };
  }

  async reset(): Promise<void> {
    await this.request('resetDone', { type: 'reset' });
  }

  async snapshot(): Promise<SolutionSnapshot> {
    return (await this.request('snapshot', { type: 'snapshot' })).snapshot;
  }

  async export(format: string, render: RenderSettings, options: ExportOptions): Promise<string> {
    return (await this.request('exported', { type: 'export', format, render, options })).text;
  }

  async render(width: number, height: number, render: RenderSettings): Promise<Uint8Array> {
    return (await this.request('rendered', { type: 'render', width, height, render })).rgba;
  }

  async toProject(source: SourceMetadata, render: RenderSettings): Promise<string> {
    return (await this.request('project', { type: 'toProject', source, render })).json;
  }

  async applyProject(json: string): Promise<ResponseOf<'projectApplied'>> {
    return this.request('projectApplied', { type: 'applyProject', json });
  }

  async physicalPlan(options: PhysicalOptions): Promise<PhysicalPlan> {
    return (await this.request('physicalPlan', { type: 'physicalPlan', options })).plan;
  }

  async stats(): Promise<EngineStats> {
    return (await this.request('stats', { type: 'stats' })).stats;
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    try {
      await this.request('disposed', { type: 'dispose' });
    } finally {
      this.disposed = true;
      this.rejectAll(new Error('engine client is disposed'));
      this.worker.terminate();
    }
  }
}
